import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Grid,
  Card,
  Group,
  Badge,
  Button,
  Stack,
  Alert,
  ActionIcon,
  Tooltip,
  Select,
  Switch,
  Indicator,
} from '@mantine/core';
import {
  IconCamera,
  IconCameraOff,
  IconPlayerPlay,
  IconPlayerPause,
  IconRefresh,
  IconSettings,
  IconAlertTriangle,
  IconMaximize,
  IconVolume,
  IconVolumeOff,
} from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getChildCameras, getCameraStatus } from '../../services/parentService';
import { SmartLoader } from '../../components/common/LoadingStates';
import RTSPVideoPlayer from '../../components/video/RTSPVideoPlayer';

const ChildCameraPage: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();
  const queryClient = useQueryClient();

  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [streamQuality, setStreamQuality] = useState('720p');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch child's cameras
  const {
    data: camerasData,
    isLoading: camerasLoading,
    error: camerasError,
    refetch: refetchCameras,
  } = useQuery({
    queryKey: ['child-cameras', childId],
    queryFn: () => getChildCameras(childId!),
    enabled: !!childId,
    refetchInterval: autoRefresh ? 30000 : false, // Refresh every 30 seconds if auto-refresh is on
  });

  // Note: Stream data is now handled by RTSPVideoPlayer component
  // const {
  //   data: streamData,
  //   isLoading: streamLoading,
  //   error: streamError,
  // } = useQuery({
  //   queryKey: ['camera-stream', childId, selectedCameraId],
  //   queryFn: () => getCameraStream(childId!, selectedCameraId!),
  //   enabled: !!childId && !!selectedCameraId && isStreaming,
  //   refetchInterval: 10000, // Refresh stream info every 10 seconds
  // });

  // Fetch selected camera status
  const {
    data: statusData,
  } = useQuery({
    queryKey: ['camera-status', childId, selectedCameraId],
    queryFn: () => getCameraStatus(childId!, selectedCameraId!),
    enabled: !!childId && !!selectedCameraId,
    refetchInterval: 15000, // Refresh status every 15 seconds
  });

  // Auto-select first camera when cameras load
  useEffect(() => {
    if (camerasData?.data && camerasData.data.length > 0 && !selectedCameraId) {
      setSelectedCameraId(camerasData.data[0].id);
    }
  }, [camerasData, selectedCameraId]);

  // Memoize the status change handler to prevent unnecessary re-renders
  const handleStatusChange = useCallback((status: 'loading' | 'playing' | 'paused' | 'error' | 'stopped') => {
    console.log('Stream Status:', status);
    // Use setTimeout to avoid setState during render
    setTimeout(() => {
      setIsStreaming(status === 'playing');
    }, 0);
  }, []);

  // Memoize the error handler to prevent unnecessary re-renders
  const handleStreamError = useCallback((error: string) => {
    console.error('RTSP Stream Error:', error);
    notifications.show({
      title: 'Stream Error',
      message: 'Unable to connect to camera stream. Please try again.',
      color: 'red',
    });
  }, []);

  const handleStartStream = () => {
    if (!selectedCameraId) {
      notifications.show({
        title: 'No Camera Selected',
        message: 'Please select a camera to start streaming',
        color: 'orange',
      });
      return;
    }
    setIsStreaming(true);
    notifications.show({
      title: 'Stream Starting',
      message: 'Connecting to camera stream...',
      color: 'blue',
    });
  };

  const handleStopStream = () => {
    setIsStreaming(false);
    notifications.show({
      title: 'Stream Stopped',
      message: 'Camera stream has been disconnected',
      color: 'gray',
    });
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['child-cameras', childId] });
    if (selectedCameraId) {
      queryClient.invalidateQueries({ queryKey: ['camera-status', childId, selectedCameraId] });
    }
    notifications.show({
      title: 'Refreshed',
      message: 'Camera information updated',
      color: 'green',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'red';
      case 'maintenance': return 'yellow';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status: string, isOnline: boolean) => {
    if (!isOnline) return <IconCameraOff size={16} />;
    return status === 'active' ? <IconCamera size={16} /> : <IconCameraOff size={16} />;
  };

  if (camerasLoading) {
    return <SmartLoader isLoading={true}>Loading cameras...</SmartLoader>;
  }

  if (camerasError) {
    return (
      <Container size="lg" py="xl">
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Error Loading Cameras"
          color="red"
        >
          Unable to load camera information. Please try again later.
          <Button variant="light" size="sm" mt="sm" onClick={() => refetchCameras()}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  const cameras = camerasData?.data || [];
  const selectedCamera = cameras.find(c => c.id === selectedCameraId);

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Classroom Camera</Title>
            <Text c="dimmed">
              {camerasData?.meta.child_name} - {camerasData?.meta.class_name}
            </Text>
          </div>
          <Group>
            <Tooltip label="Auto-refresh camera status">
              <Switch
                label="Auto-refresh"
                checked={autoRefresh}
                onChange={(event) => setAutoRefresh(event.currentTarget.checked)}
              />
            </Tooltip>
            <ActionIcon variant="light" onClick={handleRefresh}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {cameras.length === 0 ? (
          <Alert
            icon={<IconCamera size={16} />}
            title="No Cameras Available"
            color="blue"
          >
            No cameras are currently available for your child's classroom.
            Please contact the school if you believe this is an error.
          </Alert>
        ) : (
          <Grid>
            {/* Camera Selection and Controls */}
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="md">
                {/* Camera Selection */}
                <Card withBorder>
                  <Stack gap="sm">
                    <Text fw={500}>Available Cameras</Text>
                    <Select
                      placeholder="Select a camera"
                      value={selectedCameraId}
                      onChange={setSelectedCameraId}
                      data={cameras.map(camera => ({
                        value: camera.id,
                        label: `${camera.name} (${camera.area_name})`,
                      }))}
                    />
                  </Stack>
                </Card>

                {/* Camera Status */}
                {selectedCamera && statusData && (
                  <Card withBorder>
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Text fw={500}>Camera Status</Text>
                        <Indicator
                          color={statusData.is_online ? 'green' : 'red'}
                          size={8}
                        >
                          <Badge
                            color={getStatusColor(statusData.status)}
                            leftSection={getStatusIcon(statusData.status, statusData.is_online)}
                          >
                            {statusData.is_online ? 'Online' : 'Offline'}
                          </Badge>
                        </Indicator>
                      </Group>

                      <Stack gap="xs">
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">Location:</Text>
                          <Text size="sm">{statusData.area_name}</Text>
                        </Group>
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">Recording:</Text>
                          <Badge color={statusData.is_recording ? 'green' : 'gray'} size="sm">
                            {statusData.is_recording ? 'Active' : 'Inactive'}
                          </Badge>
                        </Group>
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">AI Detection:</Text>
                          <Badge color={statusData.ai_detection_enabled ? 'blue' : 'gray'} size="sm">
                            {statusData.ai_detection_enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </Group>
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">Recent Activities:</Text>
                          <Text size="sm" fw={500}>{statusData.recent_activities_count}</Text>
                        </Group>
                      </Stack>
                    </Stack>
                  </Card>
                )}

                {/* Stream Controls */}
                <Card withBorder>
                  <Stack gap="sm">
                    <Text fw={500}>Stream Controls</Text>

                    <Select
                      label="Quality"
                      value={streamQuality}
                      onChange={(value) => setStreamQuality(value || '720p')}
                      data={[
                        { value: '480p', label: '480p (Low)' },
                        { value: '720p', label: '720p (Medium)' },
                        { value: '1080p', label: '1080p (High)' },
                      ]}
                    />

                    <Group grow>
                      {!isStreaming ? (
                        <Button
                          leftSection={<IconPlayerPlay size={16} />}
                          onClick={handleStartStream}
                          disabled={!selectedCamera?.has_live_stream}
                        >
                          Start Stream
                        </Button>
                      ) : (
                        <Button
                          leftSection={<IconPlayerPause size={16} />}
                          onClick={handleStopStream}
                          color="red"
                        >
                          Stop Stream
                        </Button>
                      )}
                    </Group>

                    <Group grow>
                      <ActionIcon.Group>
                        <ActionIcon
                          variant={isMuted ? 'filled' : 'light'}
                          onClick={() => setIsMuted(!isMuted)}
                        >
                          {isMuted ? <IconVolumeOff size={16} /> : <IconVolume size={16} />}
                        </ActionIcon>
                        <ActionIcon variant="light">
                          <IconMaximize size={16} />
                        </ActionIcon>
                        <ActionIcon variant="light">
                          <IconSettings size={16} />
                        </ActionIcon>
                      </ActionIcon.Group>
                    </Group>
                  </Stack>
                </Card>
              </Stack>
            </Grid.Col>

            {/* Video Stream */}
            <Grid.Col span={{ base: 12, md: 8 }}>
              {selectedCamera ? (
                <RTSPVideoPlayer
                  rtspUrl={selectedCamera.rtsp_url || ''}
                  cameraName={selectedCamera.name}
                  cameraId={selectedCamera.id}
                  autoPlay={isStreaming}
                  showControls={true}
                  showStatus={true}
                  height={500}
                  onError={handleStreamError}
                  onStatusChange={handleStatusChange}
                />
              ) : (
                <Card withBorder style={{ height: '500px' }}>
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                    }}
                  >
                    <Stack align="center" gap="md">
                      <IconCamera size={64} color="gray" />
                      <Text c="white" ta="center">
                        Please select a camera to view the live stream
                      </Text>
                      <Text c="gray" size="sm" ta="center">
                        Choose from the available cameras in the sidebar
                      </Text>
                    </Stack>
                  </div>
                </Card>
              )}
            </Grid.Col>
          </Grid>
        )}
      </Stack>
    </Container>
  );
};

export default ChildCameraPage;
